import * as React from 'react'
/*:: import type {ComponentType} from 'react' */
import styled from 'styled-components'
import {translate} from 'react-i18next'
import IoEmail from 'react-icons/lib/io/ios-email'
import IoIosTelephone from 'react-icons/lib/io/ios-telephone'
import IoSocialFacebok from 'react-icons/lib/io/social-facebook'
import IoSocialInstagramOutline from 'react-icons/lib/io/social-instagram-outline'
import IoSocialTwitter from 'react-icons/lib/io/social-twitter'
import {
  Link,
  LazyImg,
  SectionContent,
  styles
} from 'tutellus-ui'

export const Main = styled.main`
    margin-top: ${styles.HEADER_HEIGHT}px;
`

const Icon = styled(({className, icon}) =>
  <i className={`${className} ${icon}`} />
)`
    font-size: 1.7em;
    text-align: center;
    line-height: 1.5em;
`
Icon.displayName = 'Icon'

const LinksGroup = styled(({className, title, links = [], withImages = false}) =>
  <div className={className}>
    <div>{title}</div>
    <ul>
      {
        links.map((link, index) =>
          <li key={`${className}${index}`}>
            <Link href={link.url}>
              { withImages
                ? <LazyImg src={link.image} alt={link.title} />
                : link.title
              }
            </Link>
          </li>
        )
      }
    </ul>
  </div>
)`
    color: ${styles.colors.darkfg};
    align-content: start;
    & div {
        text-transform: uppercase;
        font-weight: bold;
        font-size: 0.9em;
        margin-bottom: 0.7em;
    }
    & ul {
        display: grid;
        grid-gap: 0.5em 0.5em;
        grid-template-columns: 1fr;
        grid-template-rows: auto;
        margin: 0 0 1.5em 0;
        padding: 0;
        list-style-type: none;

        & li {
            font-size: 0.8em;
        }

        & img {
            min-width: 100%;
            max-width: 100%;
        }
    }
    & ${Link} {
      text-decoration: none;
      color: ${styles.colors.darkfg};
    }
`
LinksGroup.displayName = 'LinksGroup'

const composeLinksFormI18n = (t, prop) =>
  JSON.parse(t(prop)).map(i18n => ({
    url: t(`${i18n}_url`),
    title: t(`${i18n}_title`),
    image: t(`${i18n}_image`)
  }))

const LinksInfo = styled(({t, className}) =>
  <LinksGroup className={className} title={t('info_links_title')}
    links={composeLinksFormI18n(t, 'info_links_order')} />
)`
    & ul {
        grid-template-columns: repeat(2, 1fr);
    }
`

const LinksTutellus = styled(({t, className}) =>
  <LinksGroup className={className} title={t('tutellus_links_title')}
    links={composeLinksFormI18n(t, 'tutellus_links_order')} />
)`
`

const LinksApps = styled(({t, className}) =>
  <LinksGroup className={className} withImages
    title={t('apps_links_title')}
    links={composeLinksFormI18n(t, 'apps_links_order')} />
)`
    & ul {
        grid-template-columns: repeat(2, 1fr);
        @media ${styles.media.tablet} {
            grid-template-columns: 1fr;
        }
    }
`

const SocialBar = styled(({t, className}) =>
  <div className={className}>
    <div dangerouslySetInnerHTML={{__html: t('copyright')}} />
    <div>
      <Link className='facebook'
        href={t(`facebook_url`)}>
        <IoSocialFacebok />
      </Link>
      <Link className='twitter'
        href={t(`twitter_url`)}>
        <IoSocialTwitter />
      </Link>
      <Link
        className='instagram'
        href={t(`instagram_url`)}>
        <IoSocialInstagramOutline />
      </Link>
    </div>
  </div>
)`
    padding-top: 1em;
    border-top: 1px solid ${styles.colors.mediumbg};
    display: grid;
    font-size: 0.75em;
    line-height: 1.25em;

    grid-template-columns: 1fr;
    color: ${styles.colors.darkfg};

    & a {
        text-decoration: none;
        color: ${styles.colors.darkfg};
    }

    & div:nth-child(2){
        display:grid;
        grid-template-columns: 30px;
        grid-auto-columns: 30px;
        grid-gap: 1em;
        justify-content: center;
        @media ${styles.media.tablet} {
            justify-content: end;
        }
        grid-auto-flow: column;
        & a {
            display: grid;
        }
    }

    @media ${styles.media.tablet} {
        grid-template-columns: auto 120px;
    }
    & ${Link} {
        display: grid;
        align-content: center;
        height: 30px;
        &.facebook,&.twitter,&.instagram {
          background: ${styles.colors.mediumbg};
          color: ${styles.colors.lightfg};
          border-radius: 50%;
          min-height: 100%;
          min-width: 100%;
          justify-self: center;
          & > * {
            justify-self: center;
            height: 1.3em;
            width: 1.3em;
          }
        }
        &.facebook {
          background: ${styles.colors.social_facebbok};
        }
        &.twitter {
          background: ${styles.colors.social_twitter};
        }
        &.instagram {
          background: ${styles.colors.social_instagram};
        }
    }
`
SocialBar.displayName = 'SocialBar'

export const Footer = styled(translate('footer')(({className, t}) => {
  return (
    <footer className={className}>
      <SectionContent>
        <LinksInfo t={t} />
        <LinksTutellus t={t} />
        <LinksApps t={t} />
        <div>
          <ContactForm icon={<IoEmail />}
            title={t('contact_email_title')}
            description={
              <a target='_blank'
                href={`mailto:${t('contact_email_description')}`}>
                { t('contact_email_description') }
              </a>
            } />
          <ContactForm icon={<IoIosTelephone />}
            title={t('contact_phone_title')}
            description={
              <a href={`tel:${t('contact_phone_description').replace(/\s+/g, '')}`}>
                { t('contact_phone_description') }
              </a>
            } />
        </div>
        <SocialBar t={t} />
      </SectionContent>
    </footer>
  )
}))`
  border-top: 1px solid ${styles.colors.lightborder};
    & ${SectionContent} {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto;
        grid-gap: 0.5em 0.5em;
        padding: 1em;
        @media ${styles.media.tablet} {
            padding: 3em 1em 2em;
            grid-template-columns: 4fr 2fr 3fr 3fr;
            grid-gap: 1em 1em;
            & ${SocialBar} {
                grid-column: 1 / -1;
            }
        }
        @media ${styles.media.laptop} {
            grid-template-columns: 4fr 2fr 2fr 4fr;
            grid-gap: 1em 2em;
        }
        & > div {
            display: grid;
            grid-gap: 0.5em 0.5em;
        }
    }
`
Footer.displayName = 'Footer'

const ContactForm = styled(({className, icon, title, description}) =>
  <div className={className}>
    <div>{ icon }</div>
    <div>
      <div>{ title }</div>
      <div>{ description }</div>
    </div>
  </div>
)`
    display: grid;
    grid-template-columns: 2.5em 1fr;
    grid-column-gap: 0.5em;
    background: ${styles.colors.lightbg};
    color: ${styles.colors.darkfg};
    border: 1px solid ${styles.colors.mediumbg};
    border-radius: 3px;
    padding: 1em;

    & > div:first-child {
        display: grid;
        grid-template-columns: 1fr;
        background: ${styles.colors.mediumbg};
        color: ${styles.colors.title_decoration};
        border-radius: 50%;
        height: 40px;
        align-content: center;
        & * {
          justify-self: center;
          height: 1.5em;
          width: 1.5em;
        }
    }

    & > div:last-child {
        display: grid;
        grid-template-columns: 1fr;
        grid-row-gap: 0.1em;
        & div:nth-child(1) {
            font-size: 0.9em;
        }
        & div:nth-child(2) {
            font-size: 1em;
            font-weight: bold;
        }
    }

    & a {
        color: ${styles.colors.darkfg};
        text-decoration: none;
    }

`
ContactForm.displayName = 'ContactForm'



// WEBPACK FOOTER //
// ./src/pages/Layout.js